import { useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { openRecordByIdInModal } from '@app/components/RecordModal/model/recordModalSlice';
import type {
  FilterItemType,
  VisitsHistoryProps,
} from '@app/components/VisitsHistory/types';
import { adaptVisitsData } from '@app/components/VisitsHistory/utils/adaptVisitsData';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useLazyGetClientRecordsQuery } from '@app/services/rtkQuery/Records/api';
import { groupRecords } from '@app/shared/calendar';
import { useAppDispatch } from '@app/store';
import type { NRecords } from '@app/types';

export const useVisitsHistory = ({ clientId }: VisitsHistoryProps) => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { params } = useUserPrint();
  const [getClientRecords, { isFetching, isLoading }] =
    useLazyGetClientRecordsQuery();
  const { showNotification } = useNotification();
  const [clientRecords, setClientRecords] = useState<
    NRecords.ModelClientRecord[]
  >([]);

  const isLoadingPage = useMemo(() => {
    return isLoading || isFetching;
  }, [isLoading, isFetching]);
  const [filterValue, setFilterValue] =
    useState<FilterItemType['value']>('all-visits');
  const visitTableData = useMemo(
    () => (clientRecords ? adaptVisitsData(groupRecords(clientRecords)) : []),
    [clientRecords]
  );

  const onSelect = useCallback((value: FilterItemType['value']) => {
    setFilterValue(value);
  }, []);

  const filteredVisitTableData = useMemo(() => {
    switch (filterValue) {
      case 'all-visits':
        return visitTableData;
      case 'visits-without-payment':
        return visitTableData
          .map((day) => ({
            ...day,
            records: day.records.filter((record) => record.paid === 0),
          }))
          .filter((day) => day.records.length > 0);
      default:
        return visitTableData;
    }
  }, [visitTableData, filterValue]);

  const visitsCount = useMemo(
    () =>
      filteredVisitTableData.reduce((acc, day) => acc + day.records.length, 0),
    [filteredVisitTableData]
  );

  const onOpenRecord = useCallback(
    (id: number) => {
      dispatch(openRecordByIdInModal(id));
    },
    [dispatch]
  );

  const onOpenRecordNewTab = useCallback(
    (id: number) => {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set('recordId', id.toString());

      const newSearch = currentParams.toString();
      const url = `${window.location.origin}${pathname}?${newSearch}`;

      const newTab = window.open(url, '_blank');
      if (newTab) {
        newTab?.focus();
      }
    },
    [pathname, searchParams]
  );

  const getClientRecordsHandler = useCallback(async () => {
    if (!clientId) {
      return;
    }

    try {
      const response = await getClientRecords({
        ...params,
        clientId,
      }).unwrap();
      setClientRecords(response);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [clientId, getClientRecords, params, showNotification]);

  return {
    onSelect,
    visitsCount,
    onOpenRecord,
    onOpenRecordNewTab,
    visitTableData: filteredVisitTableData,
    isLoadingPage,
    getClientRecordsHandler,
  };
};
